import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Settings } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstallationManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });
  const [editingService, setEditingService] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/config/installation-services`);
      setServices(response.data);
    } catch (error) {
      console.error("Fetch services error:", error);
      toast.error("Gabim në ngarkimin e shërbimeve");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingService) {
        await axios.put(`${API}/config/installation-services/${editingService.id}`, formData);
        toast.success("Shërbimi u përditësua me sukses");
      } else {
        await axios.post(`${API}/config/installation-services`, formData);
        toast.success("Shërbimi u shtua me sukses");
      }

      setIsModalOpen(false);
      setEditingService(null);
      setFormData({ name: "", description: "" });
      fetchServices();
    } catch (error) {
      toast.error("Gabim në ruajtjen e shërbimit");
      console.error("Save service error:", error);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("A jeni të sigurt që dëshironi të fshini këtë shërbim?")) {
      return;
    }

    try {
      await axios.delete(`${API}/config/installation-services/${id}`);
      toast.success("Shërbimi u fshi me sukses");
      fetchServices();
    } catch (error) {
      toast.error("Gabim në fshirjen e shërbimit");
      console.error("Delete service error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center">
                <Settings className="w-6 h-6 mr-2" />
                Menaxhimi i Shërbimeve të Instalimit
              </CardTitle>
              <CardDescription>
                Menaxho shërbimet dhe opsionet për instalim/formatim
              </CardDescription>
            </div>
            <Button onClick={() => {
              setEditingService(null);
              setFormData({ name: "", description: "" });
              setIsModalOpen(true);
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Shërbim i Ri
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {services.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Nuk ka shërbime të regjistruara. Kliko "Shërbim i Ri" për të shtuar.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-900">{service.name}</h3>
                      {service.description && (
                        <p className="text-sm text-slate-600 mt-1">{service.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(service)}
                      >
                        ✏️
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(service.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <Badge variant="secondary" className="mt-2">
                    {service.key || service.name.toLowerCase().replace(/ /g, '_')}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingService ? "Përditëso Shërbimin" : "Shërbim i Ri"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Emri i Shërbimit *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="p.sh: Formatizimi i Sistemit"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Përshkrimi (opsionale)</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="p.sh: Format i plotë i Windows me backup"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingService(null);
                      setFormData({ name: "", description: "" });
                    }}
                  >
                    Anulo
                  </Button>
                  <Button type="submit">
                    {editingService ? "Përditëso" : "Shto"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InstallationManager;
